import type { TFunction } from 'i18next'
import {
    ALERT_CONFIRM_CHECKED,
    AlertProtecteeGender,
    AlertVitalStatus
} from '@/api/notification/alertServerValues.ts'
import { EmergencyAlertConfirmStatus } from '@/api/notification/getNotificationSos.ts'
import { FallDetectionResultType } from '@/api/notification/getNotificationFallDetection.ts'

/** 같은 알림 값이라도 화면마다 쓰는 번역 키가 달라(영문·일문 표기가 조금씩 다르다) 화면별로 나눠 둔다. */
export type AlertLabelScope = 'bioTable' | 'sosTable' | 'fallTable' | 'bioModal' | 'sosModal'

interface IGenderKeys {
    male: string
    female: string
}

interface IResultKeys {
    emergency119: string
    malfunction: string
    etc: string
}

const GENDER_KEYS: Record<AlertLabelScope, IGenderKeys> = {
    bioTable: { male: 'notification.bio.table.gender.male', female: 'notification.bio.table.gender.female' },
    sosTable: { male: 'notification.sos.table.gender.male', female: 'notification.sos.table.gender.female' },
    fallTable: {
        male: 'notification.falldetection.table.gender.male',
        female: 'notification.falldetection.table.gender.female'
    },
    bioModal: { male: 'notification.bioModal.gender.male', female: 'notification.bioModal.gender.female' },
    sosModal: { male: 'notification.sosModal.gender.male', female: 'notification.sosModal.gender.female' }
}

const RESULT_KEYS: Record<AlertLabelScope, IResultKeys> = {
    bioTable: {
        emergency119: 'notification.bio.table.process.119',
        malfunction: 'notification.bio.table.process.malfunction',
        etc: 'notification.bio.table.process.etc'
    },
    sosTable: {
        emergency119: 'notification.sos.table.process.119',
        malfunction: 'notification.sos.table.process.malfunction',
        etc: 'notification.sos.table.process.etc'
    },
    fallTable: {
        emergency119: 'notification.falldetection.table.process.119',
        malfunction: 'notification.falldetection.table.process.malfunction',
        etc: 'notification.falldetection.table.process.etc'
    },
    bioModal: {
        emergency119: 'notification.bioModal.type.119',
        malfunction: 'notification.bioModal.type.malfunction',
        etc: 'notification.bioModal.type.etc'
    },
    sosModal: {
        emergency119: 'notification.sosModal.type.119',
        malfunction: 'notification.sosModal.type.malfunction',
        etc: 'notification.sosModal.type.etc'
    }
}

/** 서버 성별 원문('남'/'여')을 화면 문구로. 모르는 값은 그대로 돌려준다. */
export const getAlertGenderName = (t: TFunction, scope: AlertLabelScope, value: string): string => {
    switch (value) {
        case AlertProtecteeGender.MALE:
            return t(GENDER_KEYS[scope].male)
        case AlertProtecteeGender.FEMALE:
            return t(GENDER_KEYS[scope].female)
        default:
            return value
    }
}

/** 서버 조치 결과 원문('119출동'/'오작동'/'기타')을 화면 문구로. 모르는 값은 그대로 돌려준다. */
export const getAlertResultName = (t: TFunction, scope: AlertLabelScope, value: string): string => {
    switch (value) {
        case FallDetectionResultType.EMERGENCY_119:
            return t(RESULT_KEYS[scope].emergency119)
        case FallDetectionResultType.MALFUNCTION:
            return t(RESULT_KEYS[scope].malfunction)
        case FallDetectionResultType.ETC:
            return t(RESULT_KEYS[scope].etc)
        default:
            return value
    }
}

/** 서버 알림 처리 상태 원문을 화면 문구로. 모르는 값은 그대로 돌려준다. */
export const getAlertConfirmName = (t: TFunction, value?: string): string | undefined => {
    switch (value) {
        case EmergencyAlertConfirmStatus.WAIT:
            return t('notification.confirm.status.wait')
        case EmergencyAlertConfirmStatus.ASSIGN:
            return t('notification.confirm.status.manager')
        case ALERT_CONFIRM_CHECKED:
            return t('notification.confirm.status.confirmed')
        case EmergencyAlertConfirmStatus.DONE:
            return t('notification.confirm.status.done')
        default:
            return value
    }
}

/** 서버 생체 상태 원문(심박·호흡의 hrateStatus/respStatus)을 화면 문구로. 모르는 값은 그대로 돌려준다. */
export const getAlertVitalStatusName = (t: TFunction, value: string): string => {
    switch (value) {
        case AlertVitalStatus.NORMAL:
            return t('notification.bio.table.state.normal')
        case AlertVitalStatus.STABLE:
            return t('dashboard.notification.stable')
        case AlertVitalStatus.UNSTABLE:
            return t('notification.bio.table.state.unstable')
        case AlertVitalStatus.LOW:
            return t('notification.v2.vital.low')
        case AlertVitalStatus.HIGH:
            return t('notification.v2.vital.high')
        default:
            return value
    }
}
